/* eslint-disable no-console */
import * as path from "path";

import boxen from "boxen";
import chalk from "chalk";
import { readdir, unlink } from "fs-extra";

import { createAsyncEvent } from "../EventObject";
import { ELKLogger } from "../Logging/ELKLogger";
import { Logger } from "../Logging/Logger";
import {
    CommandLineArgumentsHelpRequestedError,
    InvalidCommandLineArgumentsError,
} from "../Options/CommandBasedOptionsParser";
import { runAsyncAction } from "../../../../Common/TypingUtils";

import { ConsoleApplicationContext } from "./ConsoleApplicationContext";

type EntryPoint = (argv: string[], ctx: ConsoleApplicationContext) => void | Promise<void>;

export class ConsoleApplication {
    public static onExit = createAsyncEvent();
}

async function runConsoleApplicationInternal(entryPoint: EntryPoint): Promise<void> {
    const noSaveLogs = process.argv.some(arg => arg === "dev-build-server" || arg === "build-server");
    const logger = new Logger(undefined, undefined, noSaveLogs);

    const context = new ConsoleApplicationContext(logger);
    try {
        try {
            await entryPoint(process.argv, context);
        } finally {
            await ConsoleApplication.onExit.emit();
        }
        process.exit(0);
    } catch (error) {
        if (error instanceof CommandLineArgumentsHelpRequestedError) {
            console.log(error.usageText);
            process.exit(0);
        }

        let logMessage = "";
        let stackTrace: string | undefined = "";

        if (error instanceof InvalidCommandLineArgumentsError) {
            logMessage = error.message;
            console.error(error.message);
            console.error(error.usageText);
        } else if (error instanceof Error) {
            logMessage = "FATAL ERROR";
            stackTrace = error.stack;
            console.log(chalk.red(logMessage));
            console.log(error);
            console.log(
                boxen(`Try to call: "npm i @kontur.candy/tools -g"`, { borderColor: "green", padding: 1, margin: 1 })
            );
        } else {
            // Тут спорный момент, кажется стоит логировать только Error
            logMessage = "runConsoleApplicationInternal catched unknown error";
            stackTrace = String(error);
            console.log(error);
        }
        const errorLogFileName = path.resolve(getErrorLogFileName(new Date()));
        console.log(`Full log: ${errorLogFileName}`);
        await removeAllPreviousLogFiles();
        await logger.flushFullLogIntoFile(errorLogFileName);

        ELKLogger.error(logMessage, { stackTrace: stackTrace });
    }
}

async function removeAllPreviousLogFiles(): Promise<void> {
    const directoryForLogFiles = process.cwd();
    try {
        const files = await readdir(directoryForLogFiles);
        const previousLogFileNames = files.filter(x => x.startsWith("candy-error-log-"));
        if (previousLogFileNames.length > 0) {
            for (const fileName of previousLogFileNames) {
                await unlink(path.join(directoryForLogFiles, fileName));
            }
        }
    } catch {
        // Тут лучше не падать.
    }
}

function getErrorLogFileName(date: Date): string {
    return (
        `candy-error-log-${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}` +
        `T${date.getHours()}-${date.getMinutes()}-${date.getSeconds()}.log`
    );
}

export function runConsoleApplication(entryPoint: EntryPoint): void {
    runAsyncAction(() => runConsoleApplicationInternal(entryPoint));
}
